import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";

const GALERIES = [
  {
    titre: "Créations",
    sous: "Lettres et personnages",
    url: "https://danphotographemariage.gallery.aftershoot.com/collection/cr-ations-avec-lettres-et-personnages",
    image: "/images/galerie-creations.webp",
  },
  {
    titre: "Portraits",
    sous: "Regards, émotions et noir et blanc",
    url: "https://danphotographemariage.gallery.aftershoot.com/collection/portraits-et-noir-blanc",
    image: "/images/galerie-portraits.png",
  },
  {
    titre: "Motos & Univers urbain",
    sous: "Caractère, acier et lumière",
    url: "https://danphotographemariage.gallery.aftershoot.com/collection/moto-univers-urbain",
    image: "/images/galerie-motos.webp",
  },
  {
    titre: "Le monde des chevaux",
    sous: "La force et la beauté des chevaux",
    url: "https://evasionimagesphoto.gallery.aftershoot.com/collection/le-monde-des-chevaux",
    image: "/images/galerie-chevaux.webp",
  },
];

export default function Portfolio() {
  return (
    <main data-testid="portfolio-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <SectionHeading number="01" eyebrow="Portfolio" title="Des images avant tout"
                        subtitle="Depuis plus de 13 ans, je raconte des histoires en images — portraits, créations artistiques et univers qui me passionnent. Chaque collection s'ouvre dans sa galerie complète : cliquez sur l'univers qui vous parle." />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-16" data-testid="portfolio-galleries">
          {GALERIES.map((g, i) => (
            <motion.a
              key={g.titre}
              href={g.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`gallery-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group relative block overflow-hidden"
            >
              <div className="overflow-hidden bg-[#0D0D10]">
                <img src={g.image} alt={g.titre} loading="lazy" className="img-zoom w-full h-auto block" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
              <div className="absolute inset-0 border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/60 transition-all duration-500 m-4" />
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 flex items-end justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F8F9FA] tracking-wide">{g.titre}</h3>
                  <p className="font-script text-xl lg:text-2xl text-[#D4AF37] mt-2">{g.sous}</p>
                </div>
                <div className="shrink-0 w-12 h-12 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-all duration-400">
                  <ArrowUpRight size={20} />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Saisons & Séances */}
      <section className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24" data-testid="seasons-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 border border-[#D4AF37]/25" />
              <img
                src="/images/decor-automne-original.webp"
                alt="Le décor d'automne du Studio Quatre Saisons"
                data-testid="seasons-image"
                className="w-full h-auto block"
              />
            </div>
          </Reveal>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]" data-testid="seasons-eyebrow">02 — Le studio au fil de l'année</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4" data-testid="seasons-title">
              Saisons <span className="gold-text italic">&</span> Séances
            </h2>
            <p className="font-script text-2xl text-[#D4AF37] mt-3" data-testid="seasons-subtitle">Une ambiance différente à chaque saison</p>
            <p className="text-slate-300 leading-loose mt-8 text-sm sm:text-base" data-testid="seasons-text">
              Découvrez le Studio Quatre Saisons et ses décors saisonniers, créés pour offrir des séances originales,
              chaleureuses et personnalisées.
            </p>
            <div className="gold-card p-8 mt-8" data-testid="autumn-highlight">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Automne 2026</p>
              <p className="font-serif text-xl text-[#F8F9FA] mt-3">Le décor est en place — réservez votre séance.</p>
              <p className="text-slate-400 text-sm leading-relaxed mt-3">
                Ouverture du Studio Quatre Saisons à partir du 5 octobre 2026. Les réservations seront bientôt ouvertes.
              </p>
            </div>
            <Link to="/services" data-testid="seasons-discover-button" className="btn-gold mt-10">
              Découvrir les séances <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
